import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, setUnauthorizedHandler } from "../api/client";
import type { Permission, Staff } from "../api/types";

interface SessionValue {
  staff: Staff | null;
  loading: boolean;
  can: (permission: Permission) => boolean;
  signIn: (login: string, password: string) => Promise<Staff>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Любой 401 из любого места приложения гасит сессию — дальше роутер сам
    // уводит на экран входа, и делать это в каждом обработчике не нужно.
    setUnauthorizedHandler(() => setStaff(null));

    api
      .get<Staff>("/api/auth/me")
      .then(setStaff)
      .catch(() => setStaff(null))
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (login: string, password: string) => {
    const next = await api.post<Staff>("/api/auth/login", { login, password });
    setStaff(next);
    return next;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setStaff(null);
    }
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      staff,
      loading,
      can: permission => Boolean(staff?.permissions.includes(permission)),
      signIn,
      signOut
    }),
    [staff, loading, signIn, signOut]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession вызван вне SessionProvider");
  return value;
}
